const modeQueryParamName = 'mode';

export type IframeMode = 'preload' | 'loadComponent' | 'clearStorage';

/**
 * Sets a query parameter to the iframe URL to communicate the intent behind the host's usage of the isolation iframe
 * @param mode - The mode the iframe should be loaded in, includes "preload" for warming up connections and caches and "loadComponent" which is used for loading a fluid component.
 */
export function setIframeModeQueryParameter(iframeUrl: URL, mode: IframeMode) {
  iframeUrl.searchParams.set(modeQueryParamName, mode);
}

/**
 * Gets the query parameter that conveys the intent behind the host's invocation of the isolation iframe
 */
export function getIframeModeQueryParameter(urlSearchParams: URLSearchParams): IframeMode {
  const mode = urlSearchParams.get(modeQueryParamName);

  switch (mode) {
    case 'preload':
    case 'loadComponent':
    case 'clearStorage':
      return mode;
    default:
      return 'loadComponent';
  }
}

const parentOriginQueryParamName = 'parentOrigin';

/**
 * Sets a query parameter that communicates the host origin that the isolation iframe will be expecting to receive and send post messages against.
 */
export function setParentOriginQueryParameter(iframeUrl: URL, parentOrigin: string) {
  iframeUrl.searchParams.set(parentOriginQueryParamName, parentOrigin);
}

/**
 * Gets a query parameter that communicates the host origin that the isolation iframe will be expecting to receive and send post messages against.
 */
export function getParentOriginQueryParameter(urlSearchParams: URLSearchParams) {
  return urlSearchParams.get(parentOriginQueryParamName);
}

const skipSwCacheQueryParamName = 'skipSwCache';

/**
 * The service worker supports setting a query parameter that forces it to make a network request to get the latest response instead of using the caches.
 * This API adds or removes the relevant query parameter from the iframe url.
 */
export function setSkipSwCacheParam(iframeUrl: URL, shouldSkipCache: true) {
  if (shouldSkipCache) {
    iframeUrl.searchParams.set(skipSwCacheQueryParamName, '1');
  } else {
    iframeUrl.searchParams.delete(skipSwCacheQueryParamName);
  }
}

const skipSwQueryParamName = 'skipSw';

/**
 * Sets a query parameter that indicates that the iframe should skip loading the service worker
 */
export function setSkipServiceWorkerQueryParameter(iframeUrl: URL) {
  iframeUrl.searchParams.set(skipSwQueryParamName, '1');
}

/**
 * From inside the iframe, gets whether or not the service worker should be skipped
 */
export function getSkipServiceWorker(urlSearchParams: URLSearchParams): boolean {
  return urlSearchParams.get(skipSwQueryParamName) === '1';
}
